// System health from the player's side (D-077): page speed (Web Vitals), how long a table takes to show and the live
// connection to open, connection drops, failed calls and crashes, sent in small batches to /api/telemetry for
// Admin › Health. Signed-out pages report too. Never anything typed, said or held: routes are sent with their codes
// replaced, error messages and stacks are the app's own.
import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from "web-vitals";
import { normaliseRoute, type ClientEventT, type TimingName } from "@digu/shared";

declare const __DIGU_BUILD__: string | undefined;
/** The web build this page runs, baked in by vite (`build.json` carries the same stamp for the server). */
export const BUILD: string = typeof __DIGU_BUILD__ === "string" ? __DIGU_BUILD__ : "dev";

const FLUSH_MS = 20_000, MAX_BATCH = 40, MAX_QUEUE = 200, SAME_ERROR_MAX = 5;
let queue: ClientEventT[] = [];
let started = false;
let timer: ReturnType<typeof setInterval> | null = null;
const seenErrors = new Map<string, number>();

const here = (): string => normaliseRoute(typeof location === "undefined" ? "/" : location.pathname);
const staff = (route: string): boolean => route === "/admin" || route.startsWith("/admin/");

function push(e: ClientEventT): void {
  if (queue.length >= MAX_QUEUE) return;
  queue.push(e);
  if (started && queue.length >= MAX_BATCH) flushTelemetry();
}

export function flushTelemetry(): void {
  if (!started || !queue.length) return;
  const events = queue.splice(0, MAX_BATCH);
  const conn = (navigator as Navigator & { connection?: { effectiveType?: string } }).connection?.effectiveType;
  const body = JSON.stringify({ build: BUILD, vw: Math.max(200, Math.min(8000, Math.round(window.innerWidth))), ...(conn ? { conn: conn.slice(0, 12) } : {}), events });
  try {
    void fetch("/api/telemetry", { method: "POST", credentials: "same-origin", keepalive: true, headers: { "content-type": "application/json" }, body }).catch(() => undefined);
  } catch { /* health reporting never gets in a player's way */ }
}

/** A measured wait in the app (ms), on the current screen. */
export function timing(name: TimingName, ms: number): void {
  if (!Number.isFinite(ms) || ms < 0) return;
  push({ kind: "timing", name, value: Math.min(600_000, Math.round(ms)), route: here() });
}

export function countEvent(name: "ws-drop" | "network-fail" | "shape"): void { push({ kind: "count", name, route: here() }); }

/** A crash or a failed call. The same message is sent at most five times per page load. */
export function reportError(source: "boundary" | "window" | "promise" | "api", err: unknown, extra: { ref?: string; status?: number } = {}): void {
  const e = err instanceof Error ? err : new Error(typeof err === "string" ? err : "Unknown error");
  const message = (e.message ? `${e.name}: ${e.message}` : e.name).slice(0, 300);
  const seen = seenErrors.get(message) ?? 0;
  if (seen >= SAME_ERROR_MAX) return;
  seenErrors.set(message, seen + 1);
  push({ kind: "error", source, message, ...(e.stack ? { stack: e.stack.slice(0, 2000) } : {}), route: here(), ...(extra.ref ? { ref: extra.ref.slice(0, 12) } : {}), ...(extra.status !== undefined ? { status: extra.status } : {}) });
}

/** Called by lib/api.ts for every call that failed: lost answers are counted, server errors and bad shapes reported. */
export function reportApiFailure(path: string, status: number, code: string | undefined, ref: string | undefined): void {
  if (status === 0) { countEvent("network-fail"); return; }
  if (code === "shape") { countEvent("shape"); reportError("api", new Error(`Unexpected answer from ${normaliseRoute(path.split("?")[0] ?? path)}`), { status }); return; }
  if (status >= 500) reportError("api", new Error(`${status} ${code ?? "error"} from ${normaliseRoute(path.split("?")[0] ?? path)}`), { ...(ref ? { ref } : {}), status });
}

/** Starts sending (once per page): Web Vitals, global error handlers, the flush timer. */
export function startTelemetry(): void {
  if (started || typeof window === "undefined" || window.self !== window.top) return;
  started = true;
  const loadRoute = here();
  const vital = (m: Metric, route: string) => { if (!staff(route)) push({ kind: "vital", name: m.name as "LCP", value: Math.max(0, m.value), route }); };
  // Load metrics belong to the page that loaded; INP and CLS cover the whole visit, so the screen they end on.
  onLCP((m) => vital(m, loadRoute));
  onFCP((m) => vital(m, loadRoute));
  onTTFB((m) => vital(m, loadRoute));
  onINP((m) => vital(m, here()));
  onCLS((m) => vital(m, here()));
  window.addEventListener("error", (ev) => {
    // Other origins (extensions, injected scripts) and the harmless ResizeObserver notice are not ours.
    if (ev.filename && !ev.filename.startsWith(location.origin)) return;
    if (/ResizeObserver loop/.test(ev.message)) return;
    reportError("window", ev.error ?? ev.message);
  });
  window.addEventListener("unhandledrejection", (ev) => {
    const r: unknown = ev.reason;
    if (r instanceof DOMException && r.name === "AbortError") return;
    if (r instanceof Error && (r.name === "ApiError" || r.name === "ApiFailure")) return; // already counted by the client
    reportError("promise", r);
  });
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") flushTelemetry(); });
  window.addEventListener("pagehide", flushTelemetry);
  timer = setInterval(flushTelemetry, FLUSH_MS);
}

/** Tests only. */
export function _resetTelemetry(): ClientEventT[] {
  const q = queue; queue = []; started = false; seenErrors.clear();
  if (timer) { clearInterval(timer); timer = null; }
  return q;
}
export function _queued(): ClientEventT[] { return queue; }
